/**
 * useStatsSaver — single function: save the current game + actions + shots.
 *
 * Extracted verbatim from EstadisticasNew.js handleSaveGameAndActions:
 *  - Offline → save to IndexedDB and bail
 *  - Force-checkout all active players (via roster.buildFinalTrackingForSave)
 *  - Confirm if no actions
 *  - PUT existing game OR POST new game
 *  - POST /api/stats/actions with the merged playerActions / segments payload
 *  - POST /api/stats/shot-chart if any shots accumulated
 *  - Clear IndexedDB on success
 *  - Snackbar feedback + reset callback
 *
 * The hook takes plain dependencies (the screen wires them in) so it stays
 * trivial to test and reuses the same shapes the original screen produced.
 */

import { useCallback } from 'react';
import api from '../../../api/axiosConfig';
import actionQueue from '../../../utils/actionQueue';
import { STATS_MODE } from '../constants';

export function useStatsSaver({
  groupeId,
  statsMode,
  currentGame,
  videoUrl,
  selectedVideoGame,
  actionsLog,
  gameShotChartData,
  buildFinalTrackingForSave,
  activePlayerIds = [],
  periodSeconds,
  videoCurrentTime,
  currentPeriod,
  puntuacionLocal,
  puntuacionVisitante,
  setIsSaving,
  setSaveFailed,
  showMessage,
  onAfterSave,
  t,
}) {
  const tt = (key, opts) => (typeof t === 'function' ? t(`estadisticasNew.${key}`, opts) : key);

  return useCallback(async () => {
    if (!currentGame) {
      showMessage(tt('noGameConfigured'), 'error');
      return;
    }

    // Offline path — persist to IndexedDB and exit
    if (typeof navigator !== 'undefined' && !navigator.onLine) {
      actionQueue.saveGameState(groupeId, {
        statsMode,
        currentGame,
        actionsLog,
        playerTimeTracking: buildFinalTrackingForSave({ periodSeconds, videoCurrentTime, currentPeriod }),
        currentPeriod,
        periodSeconds,
        puntuacionLocal,
        puntuacionVisitante,
        videoUrl,
        selectedVideoGame,
        gameInProgress: true,
      });
      showMessage(tt('savedLocally'), 'warning');
      setSaveFailed(true);
      return;
    }

    // Force checkout pre-snapshot
    const finalPlayerTimeTracking = buildFinalTrackingForSave({
      periodSeconds,
      videoCurrentTime,
      currentPeriod,
    });

    if (actionsLog.length === 0) {
      // eslint-disable-next-line no-alert
      if (typeof window !== 'undefined' && !window.confirm(tt('noActionsConfirm'))) {
        return;
      }
    }

    setIsSaving(true);
    setSaveFailed(false);

    const finalScore = `${puntuacionLocal}-${puntuacionVisitante}`;

    try {
      let gameId;

      if (statsMode === STATS_MODE.VIDEO && currentGame.existingGameId) {
        gameId = currentGame.existingGameId;
        await api.put(`/api/stats/games/${gameId}`, { score: finalScore });
      } else {
        const scoreToSave = (currentGame.isNewGame && currentGame.score) ? currentGame.score : finalScore;
        const gameData = {
          groupe_id: groupeId,
          game_label: currentGame.game_label,
          adversary_name: currentGame.adversary_name,
          number_of_periods: currentGame.number_of_periods,
          period_duration: currentGame.period_duration,
          game_date: currentGame.game_date || new Date().toISOString().split('T')[0],
          game_location: currentGame.game_location,
          score: scoreToSave,
        };
        if (currentGame.isNewGame && videoUrl) {
          gameData.video_url = videoUrl;
        }
        if (currentGame.notes) {
          gameData.notes = currentGame.notes;
        }
        const response = await api.post('/api/stats/games', gameData);
        gameId = response.data.gameId;
      }

      // Build payload — one entry per playerId, with their actions and segments
      const allPlayerIds = new Set([
        ...Object.keys(finalPlayerTimeTracking).map((pid) => Number(pid)),
        ...actionsLog.map((a) => a.playerId),
      ]);

      const activeIdSet = new Set(activePlayerIds.map((id) => Number(id)));

      const finalActions = Array.from(allPlayerIds).map((pid) => {
        const tracking = finalPlayerTimeTracking[pid] || { realTimeSegments: [], videoTimeSegments: [] };
        const playerActions = actionsLog
          .filter((act) => act.playerId === pid)
          .map((act) => ({
            action: act.action,
            actionCode: act.actionCode || act.action,
            period: act.period,
            timeSec: act.timeSec,
            videoTimestamp: act.videoTimestamp,
            realTimeTimestamp: act.realTimeTimestamp,
            isLiveMode: act.isLiveMode,
            isVideoMode: act.isVideoMode,
            ...(act.qualifiers ? { qualifiers: act.qualifiers } : {}),
          }));

        return {
          playerId: pid,
          actions: playerActions,
          realTimeSegments: tracking.realTimeSegments,
          videoTimeSegments: tracking.videoTimeSegments,
          wasActive: activeIdSet.has(Number(pid)),
        };
      });

      await api.post('/api/stats/actions', { game_id: gameId, actions: finalActions });

      if (gameShotChartData && gameShotChartData.length > 0) {
        try {
          await api.post('/api/stats/shot-chart', { game_id: gameId, shots: gameShotChartData });
        } catch (shotErr) {
          console.error('Error saving shot chart:', shotErr);
        }
      }

      actionQueue.clearGameState(groupeId);
      showMessage(tt('gameSaved'), 'success');

      if (typeof onAfterSave === 'function') {
        setTimeout(() => onAfterSave(), 2000);
      }
    } catch (error) {
      console.error('Error saving game:', error);
      setIsSaving(false);
      setSaveFailed(true);
      const networkError = (typeof navigator !== 'undefined' && !navigator.onLine)
        || error.message === 'Network Error'
        || error.code === 'ERR_NETWORK';
      showMessage(tt(networkError ? 'connectionError' : 'saveError'), 'error');
    }
  }, [
    groupeId, statsMode, currentGame, videoUrl, selectedVideoGame,
    actionsLog, gameShotChartData, buildFinalTrackingForSave, activePlayerIds,
    periodSeconds, videoCurrentTime, currentPeriod,
    puntuacionLocal, puntuacionVisitante,
    setIsSaving, setSaveFailed, showMessage, onAfterSave, tt,
  ]);
}
